import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, Square, Volume2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import ConsentGate from "@/components/ConsentGate";
import Waveform from "@/components/Waveform";
import BundleHint from "@/components/BundleHint";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { useI18n } from "@/context/I18nContext";
import { dimLabel } from "@/lib/practiceLabels";


function ScoreRing({ label, value }) {
  const r = 42, c = 2 * Math.PI * r;
  const color = value >= 80 ? "#A7F3D0" : value >= 55 ? "#FFDAB9" : "#E9D5FF";
  return (
    <div className="flex flex-col items-center">
      <svg width="110" height="110" className="-rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#E5E7EB" strokeWidth="12" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (value / 100) * c} />
        <circle cx="55" cy="55" r={r + 6} fill="none" stroke="#0A0A0A" strokeWidth="2" />
      </svg>
      <div className="-mt-[72px] font-cabinet font-extrabold text-2xl">{value}</div>
      <div className="mt-[40px] text-xs font-bold uppercase tracking-widest">{label}</div>
    </div>
  );
}

export default function Accent() {
  const { t } = useI18n();
  const [bank, setBank] = useState(null); // {supported, phrases[]} — server-owned, active profile only
  const [phrase, setPhrase] = useState(null);
  const [hearing, setHearing] = useState(false);
  const clips = useRef({});
  const shown = useRef([]); // recently shown ids → server avoids them (variety without an AI call)
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [consent, setConsent] = useState(false);
  const { recording, analyser, start, stop } = useAudioRecorder();

  const loadPhrases = async (exclude) => {
    try {
      const { data } = await api.get("/accent/phrases", { params: exclude ? { exclude } : {} });
      setBank(data); setPhrase(data.phrases?.[0] || null); setResult(null);
      shown.current = [...(data.phrases || []).map((p) => p.phrase_id), ...shown.current].slice(0, 24);
    } catch { setBank({ supported: false, phrases: [] }); }
  };
  useEffect(() => { loadPhrases(); }, []);

  const startRecording = async () => {
    if (!localStorage.getItem("fluent_consent_mic")) { setConsent(true); return; }
    try {
      setResult(null);
      await start();
    } catch { toast.error(t("pm.common.micUnavailable")); }
  };

  const stopRecording = async () => {
    const b64 = await stop();
    if (b64) score(b64);
    else toast.error(t("pm.common.noAudio"));
  };

  const score = async (audioB64) => {
    setLoading(true);
    try {
      const res = await api.post("/accent/score", { audio: audioB64, mime: "audio/webm", phrase_id: phrase.phrase_id });
      setResult(res.data);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(e?.response?.status === 400 ? (e.response.data?.detail || t("pm.accent.scoreFailed")) : t("pm.accent.scoreFailed"));
    } finally { setLoading(false); }
  };

  const hear = async () => { // shared natural-audio cache; no browser speech
    if (hearing || !phrase) return;
    setHearing(true);
    try {
      let url = clips.current[phrase.phrase_id];
      if (!url) {
        const res = await api.post(`/accent/phrases/${phrase.phrase_id}/audio`, null, { responseType: "blob" });
        url = clips.current[phrase.phrase_id] = URL.createObjectURL(res.data);
      }
      const a = new Audio(url); a.onended = () => setHearing(false); a.onerror = () => setHearing(false); await a.play();
    } catch (e) {
      setHearing(false);
      if (e?.response?.status === 402) setPaywall(true); else toast.error(t("pm.common.voiceUnavailable"));
    }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">AccentCoach</h1>
        <p className="text-gray-600">{t("pm.accent.subtitle")}</p>
        <BundleHint id="accent">{t("pm.accent.hint")}</BundleHint>

        {bank && !bank.supported && (
          <div className="nb-card p-6 mt-6 text-center" data-testid="accent-unavailable">
            <p className="font-cabinet font-bold text-xl">{t("pm.accent.unavailable")}</p>
          </div>
        )}
        {bank?.supported && phrase && (<>
        <div className="nb-card bg-peach p-6 mt-6 text-center" data-testid="accent-phrase-card">
          <div className="nb-tag bg-white mb-3">{t("pm.accent.sayThis", { level: phrase.level })}</div>
          <p className="font-cabinet font-extrabold text-2xl" dir="auto" data-testid="accent-phrase-text">{phrase.text}</p>
          <button onClick={hear} disabled={hearing} data-testid="accent-hear-btn" className="nb-btn-outline mt-3 px-4 py-2 text-sm"><Volume2 className="w-4 h-4" /> {hearing ? t("pm.accent.playing") : t("pm.accent.hear")}</button>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {bank.phrases.map((p) => (
              <button key={p.phrase_id} data-testid={`accent-phrase-${p.phrase_id}`} onClick={() => { setPhrase(p); setResult(null); }} className={`nb-tag text-[10px] ${phrase.phrase_id === p.phrase_id ? "bg-ink text-white" : "bg-white"}`} dir="auto">{p.text.slice(0, 18)}…</button>
            ))}
            <button onClick={() => loadPhrases(shown.current.join(","))} data-testid="accent-new-phrases" className="nb-tag text-[10px] bg-mint"><RefreshCw className="w-3 h-3 inline" /> {t("pm.accent.newPhrases")}</button>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          {loading ? (
            <div className="text-center"><div className="w-16 h-16 border-4 border-ink border-t-coral rounded-full animate-spin mx-auto" /><p className="font-cabinet font-bold mt-3">{t("pm.accent.scoring")}</p></div>
          ) : recording ? (
            <button onClick={stopRecording} data-testid="accent-stop-btn" className="w-24 h-24 bg-coral border-4 border-ink rounded-full shadow-hard flex items-center justify-center animate-pulse">
              <Square className="w-9 h-9 text-white fill-white" />
            </button>
          ) : (
            <button onClick={startRecording} data-testid="accent-record-btn" className="w-24 h-24 bg-mint border-4 border-ink rounded-full shadow-hard hover:shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center">
              <Mic className="w-10 h-10 stroke-[2.5]" />
            </button>
          )}
        </div>
        <p className="text-center text-sm text-gray-500 mt-3">{recording ? t("pm.accent.recording") : t("pm.accent.tapRecord")}</p>
        </>)}

        {recording && (
          <div className="nb-card p-4 mt-5" data-testid="accent-waveform">
            <Waveform analyser={analyser} active color="#FF6B6B" height={80} bars={40} />
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="nb-card mt-8 p-6" data-testid="accent-result">
            <div className="text-sm"><span className="font-bold">{t("pm.accent.weHeard")}</span> <span dir="auto">"{result.transcript}"</span></div>
            <div className="flex flex-wrap justify-around gap-4 mt-6">
              {Object.entries(result.scores || {}).map(([k, v]) => <ScoreRing key={k} label={dimLabel(t, k)} value={v} />)}
            </div>
            <div className="bg-lavender border-2 border-ink rounded-xl p-4 mt-6">
              <div className="font-cabinet font-bold">{t("pm.accent.focusOn")} {dimLabel(t, result.weakest)}</div>
              <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                {result.drills?.map((dr, i) => <li key={i}>{dr}</li>)}
              </ul>
            </div>
            <button onClick={() => setResult(null)} className="nb-btn-primary w-full mt-5 py-3"><RefreshCw className="w-5 h-5" /> {t("pm.accent.tryAgain")}</button>
          </motion.div>
        )}
      </div>
      <ConsentGate type="mic" open={consent} onGrant={() => { setConsent(false); startRecording(); }} onClose={() => setConsent(false)} />
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("pm.common.proFeature", { name: "AccentCoach" })} />
    </AppLayout>
  );
}
